import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import type { SystemAlias, SystemAliasInput } from "../../../api/useAliasApi";

const IMAGE_SET_TOKENS = ["ext", "pano", "man", "crop", "feat", "uc", "bgrpano", "bgr"] as const;
const ALIAS_PATTERN = /^[a-z0-9-]{2,64}$/;

type Mode = "create" | "edit";

type Props = {
  open: boolean;
  mode: Mode;
  systemCode: string;
  existing?: SystemAlias | null;
  onClose: () => void;
  onSubmit: (input: SystemAliasInput) => Promise<void>;
};

const tokensFromImageSet = (imageSet?: string | null): string[] => {
  if (!imageSet) return [];
  return imageSet.split("-").filter((token) => IMAGE_SET_TOKENS.includes(token as typeof IMAGE_SET_TOKENS[number]));
};

const AliasDialog: React.FC<Props> = ({ open, mode, systemCode, existing, onClose, onSubmit }) => {
  const [alias, setAlias] = useState("");
  const [imageTokens, setImageTokens] = useState<string[]>([]);
  const [commonName, setCommonName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setAlias(existing?.alias ?? "");
    setImageTokens(tokensFromImageSet(existing?.imageSet));
    setCommonName(existing?.commonName ?? "");
    setServerError(null);
  }, [open, existing]);

  const aliasError = useMemo(() => {
    if (mode === "edit") return null;
    if (!alias) return null;
    if (!ALIAS_PATTERN.test(alias)) {
      return "Lowercase letters, digits, and hyphens only (2-64 chars).";
    }
    return null;
  }, [alias, mode]);

  const canSubmit = mode === "edit" || (alias && !aliasError);

  const handleSubmit = async () => {
    setSubmitting(true);
    setServerError(null);
    try {
      await onSubmit({
        alias: alias.toLowerCase(),
        systemCode,
        imageSet: imageTokens.length ? imageTokens.join("-") : null,
        commonName: commonName.trim() || null,
      });
      onClose();
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to save alias.";
      setServerError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      data-cy="alias-dialog"
    >
      <DialogTitle>{mode === "create" ? "Add Alias" : `Edit ${existing?.alias ?? ""}`}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <TextField
            data-cy="alias-dialog-alias-input"
            label="Alias"
            value={alias}
            onChange={(e) => setAlias(e.target.value.toLowerCase())}
            disabled={mode === "edit"}
            required
            inputProps={{ maxLength: 64 }}
            placeholder="devunit"
            helperText={aliasError ?? "Used in /iframe/{alias}/{assetId}. Lowercase only."}
            error={Boolean(aliasError)}
            fullWidth
          />

          <TextField
            label="System Code"
            value={systemCode}
            disabled
            fullWidth
            helperText="Aliases are scoped to the current system."
          />

          <TextField
            data-cy="alias-dialog-image-set-select"
            select
            label="Image Set"
            value={imageTokens}
            onChange={(e) => {
              const value = e.target.value as unknown as string[];
              setImageTokens(Array.isArray(value) ? value : [value]);
            }}
            SelectProps={{
              multiple: true,
              renderValue: (selected) => {
                const values = selected as string[];
                if (!values.length) return <Typography variant="body2" color="text.secondary">All images</Typography>;
                return (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {values.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                );
              },
            }}
            helperText="Leave empty for all images. Picking one token (uc or pano) drives iframe view counters."
            fullWidth
          >
            {IMAGE_SET_TOKENS.map((token) => (
              <MenuItem key={token} value={token} data-cy={`alias-dialog-image-set-option-${token}`}>
                {token}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            data-cy="alias-dialog-common-name-input"
            label="Common Name"
            value={commonName}
            onChange={(e) => setCommonName(e.target.value)}
            placeholder="Optional"
            helperText="Internal label to remember what this alias is for."
            fullWidth
          />

          {serverError && (
            <Typography variant="body2" color="error" data-cy="alias-dialog-server-error">
              {serverError}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting} data-cy="alias-dialog-cancel-btn">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canSubmit || submitting}
          data-cy="alias-dialog-save-btn"
        >
          {mode === "create" ? "Create" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AliasDialog;
